// StateCard — unified loading / empty / error state component.
//
// E-3 (19BM · spec §3.5): "抽 <StateCard> 复用组件 (loading/empty/error 三 variant)".
//
// Problem it solves: the codebase had 4 fragmented empty-state naming conventions
//   — empty-state-rich (11+ views) / home-empty / admin-empty (6) /
//     message-center-empty-* / notif-empty-*
// each copy-pasting the same icon + title + hint structure. This component is
// the NEW standard; legacy classes migrate incrementally (3 high-frequency
// call sites in this Task, the rest as opportunistic cleanup).
//
// All three variants share structure: icon + serif title + description + action.
//   - loading: shimmer skeleton + role=status (a11y) · pairs with LoadingSkeleton
//   - empty:   icon + title + hint + optional action (CTA to next step)
//   - error:   icon + title + detail + errorId ref (19BL error handle) + retry
//
// Design: all visuals via tokens (--shadow-* navy · --color-ink-* · --font-display).
// Zero inline hex. Matches the §1.3 token-first principle.

import { type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { LoadingSkeleton } from './loading-skeleton.js';

export type StateCardVariant = 'loading' | 'empty' | 'error';

/** Heading level for the title. Session 3: most call sites use h2 (page-level
 *  empty state), but panel-embedded ones (notification / message-center) need
 *  span to avoid breaking the heading hierarchy. */
export type StateCardTitleLevel = 'h1' | 'h2' | 'span';

/** Visual size. Session 4: 'full' = the default 200px+ card (icon 48 + serif h2);
 *  'compact' = inline table-row placeholder (no card chrome, 0.82rem, span title)
 *  for admin/operator tables where the full card would dwarf the data view. */
export type StateCardSize = 'full' | 'compact';

export interface StateCardProps {
  variant: StateCardVariant;
  /** Decorative icon node (e.g. <BookmarkX size={48} />). Defaults per variant. */
  icon?: ReactNode;
  /** Primary message — rendered as a heading (level via titleLevel, default h2). */
  title: ReactNode;
  /** Secondary description / hint. */
  hint?: ReactNode;
  /** Optional action slot (button / link / CTA) rendered in the actions row. */
  action?: ReactNode;
  /** Heading level for title. Default 'h2'; use 'span' when embedded in a panel
   *  that already has its own heading, to avoid nested-heading a11y issues.
   *  'compact' size forces 'span' regardless. */
  titleLevel?: StateCardTitleLevel;
  /** Visual size. Default 'full'; 'compact' for inline table-row placeholders. */
  size?: StateCardSize;
  /** error variant only: user-referenceable handle from 19BL (err_xxx). */
  errorId?: string | null;
  /** error variant only: retry callback. Renders retry button when provided. */
  onRetry?: () => void;
  /** Retry button label (caller passes i18n string). */
  retryLabel?: string;
}

export function StateCard({
  variant,
  icon,
  title,
  hint,
  action,
  titleLevel,
  size = 'full',
  errorId,
  onRetry,
  retryLabel,
}: StateCardProps): ReactNode {
  // Default icon only for error variant (alert triangle); empty/loading defer to caller.
  const defaultIcon = variant === 'error'
    ? <AlertTriangle size={size === 'compact' ? 16 : 40} aria-hidden="true" />
    : null;

  // Session 4: compact size forces span title (inline table-row, no heading).
  const effectiveTitleLevel: StateCardTitleLevel = size === 'compact' ? 'span' : (titleLevel ?? 'h2');
  const TitleTag = effectiveTitleLevel;

  // Loading variant: delegate to LoadingSkeleton (eliminates duplicate shimmer
  // CSS + reuses its fuller a11y: role=status + aria-busy + aria-label). The
  // title becomes the skeleton's visible label so users know what's loading.
  if (variant === 'loading') {
    return <LoadingSkeleton variant="section" label={typeof title === 'string' ? title : undefined} />;
  }

  const sizeClass = size === 'compact' ? ' state-card-compact' : '';

  return (
    <div className={`state-card state-card-${variant}${sizeClass}`}>
      {icon ?? defaultIcon ? (
        <div className="state-card-icon" aria-hidden="true">{icon ?? defaultIcon}</div>
      ) : null}
      <TitleTag className="state-card-title">{title}</TitleTag>
      {hint && <p className="state-card-hint">{hint}</p>}
      {errorId && <p className="state-card-error-ref">{errorId}</p>}
      {(onRetry || action) && (
        <div className="state-card-actions">
          {action}
          {onRetry && (
            <button type="button" className="btn-secondary" onClick={onRetry}>
              <RotateCcw size={13} aria-hidden="true" /> {retryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
